<template>
  <v-card outlined>
    <!-- Filtrer cette liste (nom ou libellé), dès qu'elle est un peu longue -->
    <div v-if="keys.length > 8" class="pa-2">
      <v-text-field v-model="filter" :placeholder="$t('charging-point-tracked-keys.filter')" prepend-inner-icon="mdi-filter-variant" dense hide-details clearable class="mt-0 pt-0" style="max-width: 320px" />
    </div>
    <v-simple-table dense :height="sortedKeys.length > 12 ? height : undefined" :fixed-header="!!height && sortedKeys.length > 12">
      <thead>
        <tr>
          <th class="sortable" @click="sortBy('code')">{{ $t('charging-point-tracked-keys.key') }}<v-icon v-if="sort.by === 'code'" x-small>{{ sort.desc ? 'mdi-arrow-down' : 'mdi-arrow-up' }}</v-icon></th>
          <th v-if="informations">{{ $t('charging-point-tracked-keys.unit') }}</th>
          <th v-else>{{ $t('charging-point-tracked-keys.expected') }}</th>
          <th v-if="where">{{ $t('charging-point-tracked-keys.where') }}</th>
          <th v-if="manufacturerId">{{ $t('charging-point-tracked-keys.here') }}</th>
          <th class="text-right sortable" @click="sortBy('present')"><v-icon v-if="sort.by === 'present'" x-small>{{ sort.desc ? 'mdi-arrow-down' : 'mdi-arrow-up' }}</v-icon>{{ $t('charging-point-tracked-keys.with-key') }}</th>
          <th v-if="!informations" class="text-right sortable" @click="sortBy('deviating')"><v-icon v-if="sort.by === 'deviating'" x-small>{{ sort.desc ? 'mdi-arrow-down' : 'mdi-arrow-up' }}</v-icon>{{ $t('charging-point-tracked-keys.deviating') }}</th>
          <th style="width: 1%"></th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="!sortedKeys.length"><td colspan="7" class="text--secondary">{{ keys.length ? $t('charging-point-tracked-keys.none-found') : empty }}</td></tr>
        <tr v-for="key in sortedKeys" :key="key.kind + key.code" class="clickable" @click="$emit('open', key)">
          <td>
            <span class="mono">{{ key.code }}</span>
            <div v-if="key.label" class="text-caption text--secondary">{{ key.label }}</div>
          </td>
          <td v-if="informations">{{ key.unit || '' }}</td>
          <td v-else class="mono">{{ key.expected === null ? '' : display(key.expected) }}</td>
          <td v-if="where" class="text-caption">
            <v-chip v-for="name in manufacturersOf(key)" :key="name" x-small outlined class="mr-1 mb-1">{{ name }}</v-chip>
          </td>
          <td v-if="manufacturerId" class="text-caption">
            <div v-for="variant in variantsHere(key)" :key="variant.id">
              <v-chip x-small outlined class="mr-1">{{ $t('charging-point-tracked-keys.layers.' + variant.layer) }}</v-chip>{{ variant.layer === 'manufacturer' ? '' : variant.targetName }}
              <span v-if="variant.versionPattern" class="mono"> {{ variant.versionPattern }}</span>
              <span v-if="variant.key"> · <span class="mono">{{ variant.key }}</span></span>
              <span v-if="variant.expected !== null"> = <span class="mono">{{ display(variant.expected) }}</span></span>
              <v-btn icon x-small :aria-label="$t('delete')" @click.stop="$emit('remove-variant', key, variant)"><v-icon x-small>mdi-close</v-icon></v-btn>
            </div>
          </td>
          <td class="text-right">{{ key.present }}</td>
          <td v-if="!informations" class="text-right">
            <span v-if="key.deviating" class="orange--text font-weight-medium">{{ key.deviating }}</span>
            <span v-else-if="key.expected !== null || (manufacturerId && variantsHere(key).length)" class="success--text">0</span>
          </td>
          <td class="text-no-wrap" @click.stop>
            <slot name="actions" :item="key" />
          </td>
        </tr>
      </tbody>
    </v-simple-table>
  </v-card>
</template>

<script>
/**
 * Une liste de clés suivies : leur valeur attendue, où elles sont déclinées, les bornes qui les ont et celles en écart.
 * Un clic ouvre la répartition ; les actions de la ligne viennent de la page.
 */
export default {
  props: {
    keys: {type: Array, required: true},
    empty: {type: String, default: ''},
    // Chez un constructeur : ses déclinaisons de chaque clé.
    manufacturerId: {type: String, default: null},
    // Sur le parc, pour une clé commune : les constructeurs chez qui elle est déclinée.
    where: {type: Boolean, default: false},
    targets: {type: Array, default: () => []},
    // Une longue liste défile dans son cadre, sous son en-tête.
    height: {type: [Number, String], default: undefined},
    // Des informations : leur unité, ni valeur attendue ni écart.
    informations: {type: Boolean, default: false},
  },

  data() {
    return {
      // Un clic sur l'en-tête trie (nom, bornes qui l'ont, en écart) ; un second inverse ; sans choix, l'ordre reçu.
      sort: {by: null, desc: false},
      filter: '',
    }
  },

  computed: {
    filteredKeys() {
      const filter = (this.filter || '').trim().toLowerCase()
      return filter ? this.keys.filter(key => key.code.toLowerCase().includes(filter) || (key.label || '').toLowerCase().includes(filter)) : this.keys
    },
    sortedKeys() {
      const {by, desc} = this.sort
      if (!by) {
        return this.filteredKeys
      }
      const sorted = this.filteredKeys.slice().sort((a, b) => by === 'code'
        ? a.code.localeCompare(b.code, undefined, {sensitivity: 'base'})
        : (a[by] || 0) - (b[by] || 0) || a.code.localeCompare(b.code, undefined, {sensitivity: 'base'}))
      return desc ? sorted.reverse() : sorted
    },
  },

  methods: {
    sortBy(column) {
      // Les nombres d'abord du plus grand, les noms de A à Z.
      this.sort = this.sort.by === column ? {by: column, desc: !this.sort.desc} : {by: column, desc: column !== 'code'}
    },
    variantsHere(key) {
      return key.variants.filter(variant => variant.manufacturerId === this.manufacturerId)
    },
    manufacturersOf(key) {
      const names = key.variants.map(variant => (this.targets.find(target => target.layer === 'manufacturer' && target.id === variant.manufacturerId) || {}).name || variant.targetName)
      return [...new Set(names)].sort()
    },
    display(value) {
      return value === '' ? '""' : value
    },
  },
}
</script>

<style scoped lang="scss">
.mono {
  font-family: monospace;
}
.clickable {
  cursor: pointer;
}
.sortable {
  cursor: pointer;
  user-select: none;
}
</style>
